import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageFallback } from '@jana-technology/platform-ui'
import { Button } from '@jana-technology/platform-ui/button'
import { Card } from '@jana-technology/platform-ui/card'

import { useHasPermissions } from 'common/hooks/useHasPermissions'

import EditThresholdButtonAndModal from './RiskAssessment/EditThreshold'

const CardTitleAndLogToggle = ({
  title,
  isLogShowing,
  onLogToggle
}: {
  title: string
  isLogShowing: boolean
  onLogToggle: () => void
}) => {
  return (
    <div className='flex justify-between mb-4 font-medium'>
      <span>{title}</span>
      <div className='mr-2'>
        <button onClick={onLogToggle}>
          {isLogShowing ? 'Hide' : 'Show'} Log
        </button>
      </div>
    </div>
  )
}

const ProgressBar = ({
  progress,
  isSuccess,
  isError
}: {
  progress: number
  isSuccess?: boolean
  isError?: boolean
}) => {
  return (
    <div className='text-sm'>
      <div>Progress</div>
      <div className='my-1 h-1.5 bg-gray-200 rounded-full'>
        <div
          role='progressbar'
          className={[
            'h-1.5 rounded-full',
            !isSuccess && !isError ? 'bg-lighthouseBlue' : '',
            isSuccess ? 'bg-green' : '',
            isError ? 'bg-red' : ''
          ].join(' ')}
          style={{ width: `${progress}%` }}
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      {isSuccess && <div className='my-2 text-green'>Successful</div>}
      {isError && <div className='my-2 text-red'>Error Found</div>}
    </div>
  )
}

const ConfigurationPage = () => {
  const navigate = useNavigate()
  const hasPermission = useHasPermissions()
  const [showHealthCheckLog, setShowHealthCheckLog] = useState(false)
  const [healthCheckStatus, setHealthCheckStatus] = useState(0)

  if (!hasPermission(['healthcheck:read'])) {
    return (
      <div className='flex-1'>
        <PageFallback
          heading='Access Denied'
          paragraph="You don't have permission to access the requested page."
          primaryButtonText='Go back'
          onPrimaryClick={() => {
            navigate(-1)
          }}
        />
      </div>
    )
  }

  return (
    <div className='flex flex-1 flex-col bg-gray-100'>
      <header className='mt-8 px-28 h-32 flex items-center justify-between'>
        <h1 className='text-3xl font-medium text-lighthouseBlue'>
          Configuration
        </h1>
        <EditThresholdButtonAndModal />
      </header>
      <div className='px-16 lg:px-32 flex flex-1 gap-16 justify-center'>
        <div className='flex flex-col items-center w-128'>
          <h3 className='mb-4 text-2xl font-semibold'>Health Check</h3>
          <Card className='w-full'>
            <CardTitleAndLogToggle
              title='Health Check Status'
              isLogShowing={showHealthCheckLog}
              onLogToggle={() => {
                setShowHealthCheckLog(!showHealthCheckLog)
              }}
            />
            <div className='border mb-4 p-4'>
              <ProgressBar
                progress={healthCheckStatus}
                isSuccess={healthCheckStatus == 100}
              />
              {showHealthCheckLog && (
                <div className='text-sm'>Health Check Log Content</div>
              )}
            </div>
            <div className='flex justify-end h-10'>
              <Button
                kind='primary'
                onClick={() => {
                  let healthCheckValue = 0

                  const interval = setInterval(() => {
                    healthCheckValue += 10

                    if (healthCheckValue >= 100) {
                      clearInterval(interval)
                    }

                    setHealthCheckStatus(healthCheckValue)
                  }, 200)
                }}
              >
                Run Check
              </Button>
            </div>
          </Card>
        </div>
        <div className='flex flex-col items-center w-128'>
          <h3 className='mb-4 text-2xl font-semibold'>Risk Assessment</h3>
          <Card className='w-full'>
            <CardTitleAndLogToggle
              title='Risk Run Status'
              isLogShowing={false}
              onLogToggle={() => {}}
            />
            <div className='border mb-4 p-4'>
              <ProgressBar progress={75} />
            </div>
            <div className='flex justify-end h-10 gap-4'>
              <Button kind='primary'>Run Assessment</Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default ConfigurationPage
